/**
 * @fileoverview Syndicator Showdown — Stable Slip & Submission Gate
 * @module components/ReviewSlip
 *
 * Player-facing name is "My Stable Slip" (never "Bet Slip"/"Ticket") — this
 * is a racing ownership/stable game, not a wagering product. The component
 * and internal class names keep the historical "ReviewSlip"/"ss-review-*"
 * naming to avoid churning unrelated code paths.
 *
 * Rendered through a React portal directly under <body>, exactly like
 * BuyModal — so it is never clipped/mis-stacked by an ancestor's
 * overflow/transform/z-index, and it always renders above the mobile bottom
 * nav, the sticky game footer and the chat FAB.
 *
 * Content is grouped by race (leg, spend, floor status, runners with price/
 * equity/cost/rating/value + remove), followed by a full entry summary, then
 * a sticky footer with the submit ("Submit Stable") / fix ("Fix Races Below
 * Floor") button. Tapping a race header closes the slip and scrolls the
 * player to that race card.
 */

import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';

import { createPortal } from 'react-dom';

import {
  writeBatch,
  doc,
  collection,
  serverTimestamp,
  increment,
  getDoc,
} from 'firebase/firestore';

import { onAuthStateChanged }         from 'firebase/auth';
import { auth, db }                   from '../services/firebase';
import { useEntryState }              from '../context/EntryContext';
import { useChatDrawer }              from '../context/ChatDrawerContext';
import { calculateFieldPrices }       from '@ss/shared';
import { MAX_FIELD_COVERAGE }         from '@ss/shared/constants';
import { getValueBadgeMeta }          from '../utils/valueBadge';
import { computeSelectionFingerprint, sha256 } from '@ss/shared/hashUtils';
import './ReviewSlip.css';

const fmt = (v) => '$' + Math.round(v || 0).toLocaleString('en-AU');

// ─── Value badge pill (mirrors RunnerRow/BuyModal) ────────────────────────────

const ValuePill = ({ valueBadge, valueBadgeMeta }) => {
  if (!valueBadge || valueBadge === 'UNKNOWN') return null;
  const meta = getValueBadgeMeta(valueBadge, valueBadgeMeta);
  return (
    <span
      className="ss-review-value-pill"
      style={{ borderColor: meta.colour, color: meta.colour, background: meta.bg }}
      title={meta.label}
    >
      {meta.label}
    </span>
  );
};

// ─── Race Allocation Summary — one block per race ─────────────────────────────

const RaceAllocationSummary = ({
  race,
  allocation,
  complianceData,
  runners,
  pricingMap,
  perRaceFloor,
  onRemoveRunner,
  onNavigate,
  canEdit,
}) => {
  const selectedRunners = Object.entries(allocation?.runners || {});
  const isCompliant     = complianceData?.isCompliant;
  const meetsFloor      = complianceData?.meetsFloor;
  const shortfall       = complianceData?.floorShortfall || 0;
  const activeRunnerCount = complianceData?.activeRunnerCount || 0;
  const maxRunners      = Math.max(1, Math.floor(activeRunnerCount * MAX_FIELD_COVERAGE));
  const totalSpentOnRace = allocation?.totalSpentOnRace || 0;

  const runnerLookup = useMemo(() => {
    const map = {};
    (runners || []).forEach((r) => { map[r.id] = r; });
    return map;
  }, [runners]);

  return (
    <div className={`ss-review-race ${isCompliant ? '' : 'ss-review-race--warning'}`}>

      {/* Race header — tap to jump to this race on the selection page */}
      <button
        type="button"
        className="ss-review-race-header"
        onClick={() => onNavigate?.(race.id)}
        aria-label={`Go to Leg ${race.raceNumber}: ${race.raceName || ''}`}
      >
        <div className="ss-review-race-id">
          <span className="ss-review-leg-num">Leg {race.raceNumber}</span>
          <span className="ss-review-race-name">{race.raceName || `Race ${race.raceNumber}`}</span>
        </div>
        <div className="ss-review-race-right">
          <span className="ss-review-race-spend">
            {fmt(totalSpentOnRace)}
          </span>
          {isCompliant
            ? <span className="ss-review-status ss-review-status--ok">✓</span>
            : <span className="ss-review-status ss-review-status--warn">⚠</span>
          }
        </div>
      </button>

      {/* Floor / runner-count sub-row */}
      <div className="ss-review-race-subrow">
        <span>Spent {fmt(totalSpentOnRace)} / Floor {fmt(perRaceFloor)}</span>
        <span>{allocation?.runnersPicked || 0} / {maxRunners} max runners</span>
      </div>

      {!meetsFloor && shortfall > 0 && (
        <button
          type="button"
          className="ss-review-shortfall-banner ss-review-shortfall-banner--tappable"
          onClick={() => onNavigate?.(race.id)}
          aria-label={`Go to Leg ${race.raceNumber}, which needs ${fmt(shortfall)} more`}
        >
          ⚠️ {fmt(shortfall)} needed
          <span className="ss-review-shortfall-arrow">→</span>
        </button>
      )}

      {/* Runner selections */}
      {selectedRunners.length === 0 ? (
        <p className="ss-review-no-selections">No runners selected for this race.</p>
      ) : (
        <div className="ss-review-runners">
          {selectedRunners.map(([runnerId, runnerAlloc]) => {
            const runner = runnerLookup[runnerId];
            const price  = pricingMap?.[runnerId];
            return (
              <div key={runnerId} className="ss-review-runner-row">
                <div className="ss-review-runner-left">
                  {runner?.runnerNumber && (
                    <span className="ss-review-runner-num">{runner.runnerNumber}</span>
                  )}
                  <div className="ss-review-runner-name-col">
                    <span className="ss-review-runner-name">
                      {runner?.name || runnerId}
                    </span>
                    {(runner?.displayRating != null || (runner?.valueBadge && runner.valueBadge !== 'UNKNOWN')) && (
                      <span className="ss-review-runner-badges">
                        {runner?.displayRating != null && (
                          <span className="ss-review-runner-rating">{Math.round(runner.displayRating)}</span>
                        )}
                        <ValuePill valueBadge={runner?.valueBadge} valueBadgeMeta={runner?.valueBadgeMeta} />
                      </span>
                    )}
                  </div>
                </div>
                <div className="ss-review-runner-right">
                  {price != null && (
                    <span className="ss-review-runner-price" title="Price per 1% equity">
                      {fmt(price)}/1%
                    </span>
                  )}
                  <span className="ss-review-equity">
                    {runnerAlloc.equityPercent?.toFixed(2)}%
                  </span>
                  <span className="ss-review-cost">
                    {fmt(runnerAlloc.purchaseCost)}
                  </span>
                  {canEdit && onRemoveRunner && (
                    <button
                      type="button"
                      className="ss-review-runner-remove"
                      onClick={(e) => { e.stopPropagation(); onRemoveRunner(race.id, runnerId); }}
                      aria-label={`Remove ${runner?.name || 'runner'} from Leg ${race.raceNumber}`}
                      title="Remove this runner"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Race footer totals */}
      <div className="ss-review-race-footer">
        <span>Race total: <strong>{fmt(totalSpentOnRace)}</strong></span>
        {!meetsFloor && shortfall > 0 && (
          <span className="ss-review-race-footer-shortfall">Shortfall: {fmt(shortfall)}</span>
        )}
      </div>
    </div>
  );
};

// ─── Selection Fingerprint Box ────────────────────────────────────────────────

const SelectionFingerprintBox = ({ userId, slateId, allocations }) => {
  const [fingerprints, setFingerprints] = useState({});
  const [computing,    setComputing]    = useState(false);

  useEffect(() => {
    if (!userId || !slateId) return;
    const raceIds = Object.keys(allocations);
    if (raceIds.length === 0) return;

    setComputing(true);

    Promise.all(
      raceIds.map(async (raceId) => {
        const hash = await computeSelectionFingerprint(
          userId, slateId, raceId, allocations[raceId]
        );
        return [raceId, hash];
      })
    ).then((results) => {
      const fp = {};
      results.forEach(([raceId, hash]) => { fp[raceId] = hash; });
      setFingerprints(fp);
      setComputing(false);
    }).catch(() => setComputing(false));
  }, [userId, slateId, allocations]);

  const [masterFingerprint, setMasterFingerprint] = useState(null);

  useEffect(() => {
    const hashes = Object.values(fingerprints);
    if (hashes.length === 0) return;

    const sorted = hashes.slice().sort();
    sha256(sorted.join('')).then(setMasterFingerprint);
  }, [fingerprints]);

  const shortFingerprint = masterFingerprint
    ? `${masterFingerprint.substring(0, 8)}...${masterFingerprint.substring(56)}`
    : null;

  return (
    <div className="ss-fingerprint-box">
      <div className="ss-fingerprint-header">
        <span className="ss-fingerprint-icon">🔐</span>
        <div>
          <span className="ss-fingerprint-title">Selection Fingerprint</span>
          <p className="ss-fingerprint-desc">
            A cryptographic snapshot of your stable. Compare this against the
            post-race manifest in My Stable to verify your selections were
            recorded unchanged.
          </p>
        </div>
      </div>

      <div className="ss-fingerprint-value">
        {computing ? (
          <span className="ss-fingerprint-computing">Computing…</span>
        ) : shortFingerprint ? (
          <>
            <span className="ss-fingerprint-hash">{shortFingerprint}</span>
            <span className="ss-fingerprint-full" title={masterFingerprint}>
              {masterFingerprint}
            </span>
          </>
        ) : (
          <span className="ss-fingerprint-computing">—</span>
        )}
      </div>
    </div>
  );
};

// ─── Entry Summary — full metrics block above the submit button ──────────────

const EntrySummary = ({ safeRacesData, allocations, complianceSummary, financialAudit, totalRaces, totalStartingCapital, compliantCount, onNavigate }) => {
  const totalRunnersSelected = useMemo(
    () => Object.values(allocations).reduce((sum, a) => sum + (a?.runnersPicked || 0), 0),
    [allocations]
  );

  const avgPerRace = totalRaces > 0 ? financialAudit.totalSpent / totalRaces : 0;

  const { highest, lowest } = useMemo(() => {
    let hi = null, lo = null;
    safeRacesData.forEach((race) => {
      const spend = allocations[race.id]?.totalSpentOnRace || 0;
      const label = race.raceName || `Race ${race.raceNumber}`;
      if (!hi || spend > hi.spend) hi = { spend, label };
      if (!lo || spend < lo.spend) lo = { spend, label };
    });
    return { highest: hi, lowest: lo };
  }, [safeRacesData, allocations]);

  const { dearest, cheapest } = useMemo(() => {
    let hi = null, lo = null;
    safeRacesData.forEach((race) => {
      const runnersLookup = Object.fromEntries((Array.isArray(race.runners) ? race.runners : []).map(r => [r.id, r]));
      Object.entries(allocations[race.id]?.runners || {}).forEach(([runnerId, runnerAlloc]) => {
        const name = runnersLookup[runnerId]?.name || runnerId;
        const entry = {
          cost: runnerAlloc.purchaseCost || 0,
          equity: runnerAlloc.equityPercent,
          label: `${name} — ${runnerAlloc.equityPercent?.toFixed(0)}%`,
        };
        if (!hi || entry.cost > hi.cost) hi = entry;
        if (!lo || entry.cost < lo.cost) lo = entry;
      });
    });
    return { dearest: hi, cheapest: lo };
  }, [safeRacesData, allocations]);

  const belowFloorRaces = useMemo(
    () => safeRacesData
      .map((race) => ({ race, c: complianceSummary[race.id] }))
      .filter(({ c }) => c && !c.meetsFloor)
      .sort((a, b) => (b.c.floorShortfall || 0) - (a.c.floorShortfall || 0)),
    [safeRacesData, complianceSummary]
  );

  const fieldCapWarnings = useMemo(
    () => safeRacesData
      .map((race) => ({ race, c: complianceSummary[race.id] }))
      .filter(({ c }) => c && !c.meetsFieldCap),
    [safeRacesData, complianceSummary]
  );

  return (
    <div className="ss-entry-summary">
      <div className="ss-entry-summary-title">Entry Summary</div>
      <div className="ss-entry-summary-grid">
        <div className="ss-entry-summary-row">
          <span>Races complete</span>
          <strong>{compliantCount} / {totalRaces}</strong>
        </div>
        <div className="ss-entry-summary-row">
          <span>Total runners</span>
          <strong>{totalRunnersSelected}</strong>
        </div>
        <div className="ss-entry-summary-row">
          <span>Total spend</span>
          <strong>{fmt(financialAudit.totalSpent)}</strong>
        </div>
        <div className="ss-entry-summary-row">
          <span>Wallet remaining</span>
          <strong>{fmt(financialAudit.remainingWallet)}</strong>
        </div>
        <div className="ss-entry-summary-row">
          <span>Average per race</span>
          <strong>{fmt(avgPerRace)}</strong>
        </div>
        {highest && (
          <div className="ss-entry-summary-row">
            <span>Biggest race spend</span>
            <strong>{highest.label} — {fmt(highest.spend)}</strong>
          </div>
        )}
        {lowest && (
          <div className="ss-entry-summary-row">
            <span>Smallest race spend</span>
            <strong>{lowest.label} — {fmt(lowest.spend)}</strong>
          </div>
        )}
        {dearest && (
          <div className="ss-entry-summary-row">
            <span>Dearest runner</span>
            <strong>{dearest.label} — {fmt(dearest.cost)}</strong>
          </div>
        )}
        {cheapest && (
          <div className="ss-entry-summary-row">
            <span>Cheapest runner</span>
            <strong>{cheapest.label} — {fmt(cheapest.cost)}</strong>
          </div>
        )}
      </div>

      {belowFloorRaces.length > 0 && (
        <button
          type="button"
          className="ss-entry-summary-warning ss-entry-summary-warning--tappable"
          onClick={() => onNavigate?.(belowFloorRaces[0].race.id)}
          aria-label={`Go to ${belowFloorRaces[0].race.raceName || `Race ${belowFloorRaces[0].race.raceNumber}`}, which needs ${fmt(belowFloorRaces[0].c.floorShortfall)} more`}
        >
          ⚠ Below floor: {belowFloorRaces[0].race.raceName || `Race ${belowFloorRaces[0].race.raceNumber}`} needs {fmt(belowFloorRaces[0].c.floorShortfall)} more
          {belowFloorRaces.length > 1 ? ` (+${belowFloorRaces.length - 1} more race${belowFloorRaces.length - 1 > 1 ? 's' : ''})` : ''}
          <span className="ss-entry-summary-warning-arrow">→</span>
        </button>
      )}

      {fieldCapWarnings.length > 0 && (
        <button
          type="button"
          className="ss-entry-summary-warning ss-entry-summary-warning--tappable"
          onClick={() => onNavigate?.(fieldCapWarnings[0].race.id)}
          aria-label={`Go to ${fieldCapWarnings[0].race.raceName || `Race ${fieldCapWarnings[0].race.raceNumber}`}, which exceeds the field-coverage cap`}
        >
          ⚠ Field-coverage cap exceeded in {fieldCapWarnings.map(({ race }) => race.raceName || `Race ${race.raceNumber}`).join(', ')}
          <span className="ss-entry-summary-warning-arrow">→</span>
        </button>
      )}
    </div>
  );
};

// ─── Main ReviewSlip ("My Stable Slip") Component ─────────────────────────────

export const ReviewSlip = ({
  isOpen,
  onClose,
  slateData,
  racesData,
  onSubmitted,
  onNavigateRace,
}) => {
  const {
    allocations,
    complianceSummary,
    financialAudit,
    perRaceFloor,
    totalStartingCapital,
    totalRaces,
    updateRunnerEquity,
  } = useEntryState();

  const { close: closeChat, suppressFab, unsuppressFab } = useChatDrawer();

  const [currentUser,        setCurrentUser]        = useState(null);
  const [submitState,        setSubmitState]        = useState('idle'); // idle | loading | success | error
  const [errorMessage,       setErrorMessage]       = useState(null);
  const [feeConfirmed,       setFeeConfirmed]       = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const closeRef    = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setCurrentUser);
    return unsub;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFeeConfirmed(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && closeRef.current) {
      closeRef.current.focus();
    }
  }, [isOpen]);

  // ESC key close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Prevent body scroll + suppress the floating chat FAB (and close its
  // panel) while the slip is open — never two overlapping floating layers.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    closeChat?.();
    suppressFab?.();
    return () => {
      document.body.style.overflow = '';
      unsuppressFab?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const safeRacesData  = Array.isArray(racesData) ? racesData : [];
  const totalRaceCount = safeRacesData.length || totalRaces || 0;
  const compliantCount = useMemo(
    () => safeRacesData.filter((r) => complianceSummary[r.id]?.isCompliant).length,
    [safeRacesData, complianceSummary]
  );
  const allCompliant = totalRaceCount > 0 && compliantCount === totalRaceCount;

  const runnersByRace = useMemo(() => {
    const map = {};
    safeRacesData.forEach((race) => { map[race.id] = Array.isArray(race.runners) ? race.runners : []; });
    return map;
  }, [safeRacesData]);

  // Live share-price-per-1% for every runner, per race — same source RaceCard
  // uses, so the numbers shown here never disagree with a manual buy.
  const avgSpendPerRace = slateData?.avgSpendPerRace || 100_000;
  const kMultiplier     = slateData?.kMultiplier      ?? 2.900;
  const pricingByRace = useMemo(() => {
    const out = {};
    safeRacesData.forEach((race) => {
      const prices = calculateFieldPrices(runnersByRace[race.id] || [], avgSpendPerRace, kMultiplier);
      const map = {};
      (Array.isArray(prices) ? prices : []).forEach(p => { map[p.id] = p.baseSharePrice; });
      out[race.id] = map;
    });
    return out;
  }, [safeRacesData, runnersByRace, avgSpendPerRace, kMultiplier]);

  const isFeeGateActive = slateData?.entryFeeEnabled && slateData?.entryFeeAmount > 0;
  const isButtonDisabled = submitState !== 'idle' || (isFeeGateActive && !feeConfirmed);

  const handleRemoveRunner = useCallback((raceId, runnerId) => {
    updateRunnerEquity(raceId, runnerId, 0, 0);
  }, [updateRunnerEquity]);

  const handleNavigate = useCallback((raceId) => {
    onClose();
    onNavigateRace?.(raceId);
  }, [onClose, onNavigateRace]);

  const handleLockSelections = useCallback(async () => {
    if (submitState !== 'idle') return;
    if (isFeeGateActive && !feeConfirmed) return;
    if (!allCompliant) {
      setShowComplianceModal(true);
      return;
    }
    if (!currentUser) {
      setErrorMessage('You must be signed in to submit your stable.');
      return;
    }

    setSubmitState('loading');
    setErrorMessage(null);

    const userId  = currentUser.uid;
    const slateId = slateData.id;
    const entryId = `${slateId}_${userId}`;

    try {
      const batch = writeBatch(db);

      const entryRef = doc(db, 'player_entries', entryId);
      batch.set(entryRef, {
        id:                  entryId,
        slateId,
        tournamentId:        slateData.tournamentId || null,
        userId,
        userDisplayName:     currentUser.displayName || currentUser.email || userId,

        status:              'submitted',
        submittedAt:         serverTimestamp(),
        lockedAt:            null,

        totalBankroll:       totalStartingCapital,
        totalSpent:          financialAudit.totalSpent,
        remainingBankroll:   financialAudit.remainingWallet,

        totalVirtualEarnings: 0,
        racesSettled:         0,

        raceComplianceSummary: complianceSummary,
        qualifiedForFinal:     false,
        foundingMember:        false,
        submissionHash:        null,
      }, { merge: true });

      const safeAllocations = allocations && typeof allocations === 'object' ? allocations : {};
      Object.entries(safeAllocations).forEach(([raceId, raceAlloc]) => {
        const allocRef = doc(
          collection(entryRef, 'race_allocations'),
          raceId
        );

        const activeRunnerCount = runnersByRace[raceId]?.filter(
          (r) => r.status === 'active'
        ).length ?? 0;

        batch.set(allocRef, {
          id:                  raceId,
          entryId,
          slateId,
          userId,

          status:              'open',
          lockedAt:            null,

          totalSpentOnRace:    raceAlloc.totalSpentOnRace || 0,
          runnersPicked:       raceAlloc.runnersPicked    || 0,
          activeRunnersInField: activeRunnerCount,
          fieldCoveragePercent: activeRunnerCount > 0
            ? ((raceAlloc.runnersPicked || 0) / activeRunnerCount) * 100
            : 0,
          meetsFloor:          (raceAlloc.totalSpentOnRace || 0) >= perRaceFloor,
          isFallbackAllocation: false,
          fallbackHistory:     [],

          runners:             raceAlloc.runners || {},

          earnings:            null,
          earningsBreakdown:   null,
          selectionHash:       null,
        }, { merge: false });
      });

      const existingEntry = await getDoc(entryRef);
      const isNewEntry = !existingEntry.exists();

      const slateRef = doc(db, 'game_slates', slateId);
      if (isNewEntry) {
        batch.set(slateRef, {
          activePlayerCount:     increment(1),
          totalEntriesSubmitted: increment(1),
        }, { merge: true });
      }

      await batch.commit();
      setSubmitState('success');
      onSubmitted?.(entryId);

    } catch (err) {
      console.error('[ReviewSlip] Submission failed:', err);
      setSubmitState('error');
      setErrorMessage(`Submission failed: ${err.message}. Please try again.`);
    }
  }, [
    allCompliant,
    submitState,
    isFeeGateActive,
    feeConfirmed,
    currentUser,
    slateData,
    allocations,
    complianceSummary,
    financialAudit,
    totalStartingCapital,
    perRaceFloor,
    runnersByRace,
    onSubmitted,
  ]);

  const nonCompliantRaces = safeRacesData.filter(r => !complianceSummary[r.id]?.isCompliant);
  const worstShortfall = useMemo(() => {
    if (nonCompliantRaces.length === 0) return null;
    const withShortfall = nonCompliantRaces
      .map(r => ({ race: r, shortfall: complianceSummary[r.id]?.floorShortfall || 0 }))
      .filter(x => x.shortfall > 0)
      .sort((a, b) => b.shortfall - a.shortfall);
    return withShortfall[0] || null;
  }, [nonCompliantRaces, complianceSummary]);

  if (!isOpen) return null;

  const modal = (
    <>
      {/* ── Compliance blocking modal ───────────────────────────────────── */}
      {showComplianceModal && (
        <div
          className="ss-compliance-modal-overlay"
          onClick={() => setShowComplianceModal(false)}
        >
          <div
            className="ss-compliance-modal-card"
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 22, marginBottom: 8 }}>❌</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700 }}>
              Submission Not Allowed
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#9CA3AF', lineHeight: 1.5 }}>
              The following races do not meet the minimum spend requirement:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {nonCompliantRaces.map(race => {
                const shortfall = Math.round(complianceSummary[race.id]?.floorShortfall || 0);
                const floor     = Math.round(perRaceFloor || 0);
                return (
                  <div key={race.id} style={{
                    display:      'flex',
                    justifyContent: 'space-between',
                    alignItems:   'center',
                    background:   'rgba(239,68,68,0.07)',
                    border:       '1px solid rgba(239,68,68,0.25)',
                    borderRadius: 8,
                    padding:      '10px 14px',
                    fontSize:     13,
                  }}>
                    <span style={{ color: '#F1F5F9' }}>
                      {race.raceName || `Race ${race.raceNumber}`}
                    </span>
                    <span style={{ color: '#FCA5A5', fontWeight: 600, fontSize: 12 }}>
                      {fmt(shortfall)} short of {fmt(floor)} min
                    </span>
                  </div>
                );
              })}
            </div>
            <p style={{ margin: '0 0 20px', fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
              Please purchase additional equity in these races before submitting your stable.
            </p>
            <button
              onClick={() => setShowComplianceModal(false)}
              className="ss-compliance-modal-btn"
            >
              Go Back and Fix
            </button>
          </div>
        </div>
      )}

      {/* Overlay */}
      <div
        className="stable-slip__overlay"
        onClick={submitState === 'success' ? undefined : onClose}
        role="presentation"
      />

      {/* Sheet / panel */}
      <div
        className="stable-slip__sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-slip-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile only, via CSS) */}
        <div className="ss-review-drag-handle" />

        {/* Header */}
        <div className="ss-review-header">
          <h2 id="review-slip-title" className="ss-review-title">
            My Stable Slip
          </h2>
          {submitState !== 'success' && (
            <button
              ref={closeRef}
              className="stable-slip__close"
              onClick={onClose}
              type="button"
              aria-label="Close stable slip"
            >
              ✕
            </button>
          )}
        </div>

        {submitState === 'success' ? (
          <div className="ss-review-success">
            <div className="ss-success-icon">✓</div>
            <h3 className="ss-success-title">Stable Locked In</h3>
            <p className="ss-success-desc">
              Your selections are live. Races will lock automatically
              when they jump. Check My Stable to track your earnings.
            </p>
            <div className="ss-success-close-btn-container">
              <button className="ss-success-close-btn" onClick={onClose}>
                View My Stable
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Scrollable content */}
            <div className="stable-slip__body">

              <div className={`ss-review-compliance-banner ${allCompliant ? 'ss-review-compliance-banner--ok' : 'ss-review-compliance-banner--warn'}`}>
                {allCompliant ? (
                  <span>✓ All {totalRaceCount} races are compliant and ready to submit.</span>
                ) : (
                  <span>
                    ⚠ {totalRaceCount - compliantCount} race{totalRaceCount - compliantCount > 1 ? 's' : ''} still
                    need attention before you can submit.
                  </span>
                )}
              </div>

              {/* Per-race summaries */}
              <div className="ss-review-races">
                {safeRacesData.map((race) => (
                  <RaceAllocationSummary
                    key={race.id}
                    race={race}
                    allocation={allocations[race.id]}
                    complianceData={complianceSummary[race.id]}
                    runners={runnersByRace[race.id]}
                    pricingMap={pricingByRace[race.id]}
                    perRaceFloor={perRaceFloor}
                    onRemoveRunner={handleRemoveRunner}
                    onNavigate={handleNavigate}
                    canEdit={submitState !== 'loading'}
                  />
                ))}
              </div>

              {isFeeGateActive && (
                <div className="ss-review-entry-fee">
                  <div className="ss-ef-review-header">
                    <span>💳 Entry Fee Required</span>
                    <span className="ss-ef-review-amount">
                      ${slateData.entryFeeAmount.toFixed(2)} AUD
                    </span>
                  </div>
                  <p className="ss-ef-review-desc">
                    {slateData.prizeType === 'pooled'
                      ? `This game has a $${slateData.entryFeeAmount.toFixed(2)} entry fee. Current prize pool: ${fmt(slateData.grossPrizePool || 0)}.`
                      : `This game has a $${slateData.entryFeeAmount.toFixed(2)} entry fee. Operator guaranteed prize pool: ${fmt(slateData.preFundedPrizeAmount || 0)}.`
                    }
                  </p>
                  <label className="ss-ef-review-confirm">
                    <input
                      type="checkbox"
                      checked={feeConfirmed}
                      onChange={e => setFeeConfirmed(e.target.checked)}
                    />
                    I confirm I authorize the transaction of ${slateData.entryFeeAmount.toFixed(2)} AUD from my wagering balance
                  </label>
                </div>
              )}

              {currentUser && (
                <SelectionFingerprintBox
                  userId={currentUser.uid}
                  slateId={slateData.id}
                  allocations={allocations}
                />
              )}

              <EntrySummary
                safeRacesData={safeRacesData}
                allocations={allocations}
                complianceSummary={complianceSummary}
                financialAudit={financialAudit}
                totalRaces={totalRaceCount}
                totalStartingCapital={totalStartingCapital}
                compliantCount={compliantCount}
                onNavigate={handleNavigate}
              />

            </div>

            {/* Sticky footer — submit / fix button */}
            <div className="stable-slip__footer">
              {errorMessage && (
                <p className="ss-review-error">{errorMessage}</p>
              )}
              <button
                className={`ss-lock-btn ${!isButtonDisabled && allCompliant ? 'ss-lock-btn--ready' : 'ss-lock-btn--disabled'}`}
                onClick={handleLockSelections}
                disabled={isButtonDisabled}
                aria-label={allCompliant
                  ? 'Submit your stable'
                  : 'Review races below floor before submitting'
                }
              >
                {submitState === 'loading' ? (
                  <span className="ss-lock-btn-loading">
                    <span className="ss-spinner" /> Submitting…
                  </span>
                ) : allCompliant ? (
                  `🔒 Submit Stable — ${fmt(financialAudit.totalSpent)}`
                ) : (
                  'Fix Races Below Floor'
                )}
              </button>
              {!allCompliant && worstShortfall && (
                <button
                  type="button"
                  className="ss-review-footer-note ss-review-footer-note--warn ss-review-footer-note--tappable"
                  onClick={() => handleNavigate(worstShortfall.race.id)}
                  aria-label={`Go to ${worstShortfall.race.raceName || `Race ${worstShortfall.race.raceNumber}`}, which needs ${fmt(worstShortfall.shortfall)} more`}
                >
                  {worstShortfall.race.raceName || `Race ${worstShortfall.race.raceNumber}`} needs {fmt(worstShortfall.shortfall)} more before submitting.
                  <span className="ss-review-footer-note-arrow">→</span>
                </button>
              )}
              {allCompliant && (
                <p className="ss-review-footer-note">
                  Selections can be changed until each race jumps.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );

  return createPortal(modal, document.body);
};

export default ReviewSlip;
